import { useState, useEffect, useCallback } from 'react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';

interface EducationPrefs {
  dismissedTopicIds: string[];
  disabledAutoJumpTopicIds?: string[];
}

interface UseEducationPreferencesResult {
  prefs: EducationPrefs;
  loading: boolean;
  dismissTopic: (topicId: string) => Promise<void>;
  disableAutoJump: (topicId: string) => Promise<void>;
  isTopicDismissed: (topicId: string) => boolean;
  isAutoJumpDisabled: (topicId: string) => boolean;
}

export function useEducationPreferences(userId: string, accessToken: string): UseEducationPreferencesResult {
  const [prefs, setPrefs] = useState<EducationPrefs>({ dismissedTopicIds: [], disabledAutoJumpTopicIds: [] });
  const [loading, setLoading] = useState(true);

  // Load prefs on mount
  useEffect(() => {
    if (!userId || !accessToken) return;
    
    const loadPrefs = async () => {
      try {
        // Try backend first
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/education-prefs/${userId}`,
          {
            headers: getAuthHeaders(accessToken),
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setPrefs(data);
          // Sync to local storage for offline fallback and fast initial load next time
          localStorage.setItem(`education_prefs_cache:${userId}`, JSON.stringify(data));
        } else {
          throw new Error('Backend fetch failed');
        }
      } catch (error) {
        // Fallback to local storage if backend fails
        console.warn('Failed to load education prefs from backend, using cache', error);
        
        const cached = localStorage.getItem(`education_prefs_cache:${userId}`);
        if (cached) {
          setPrefs(JSON.parse(cached));
        } else {
            // Fallback to legacy local storage key if new cache missing
            const legacy = localStorage.getItem('education_prefs');
            if (legacy) {
                const legacyData = JSON.parse(legacy);
                // Migrate to new structure if needed
                if (legacyData.dismissedTopicIds) {
                    setPrefs(legacyData);
                }
            }
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadPrefs();
  }, [userId, accessToken]);

  const dismissTopic = useCallback(async (topicId: string) => {
    // Check if already dismissed to avoid unnecessary calls
    if (prefs.dismissedTopicIds.includes(topicId)) return;

    // Optimistic update
    const newPrefs = {
      ...prefs,
      dismissedTopicIds: [...prefs.dismissedTopicIds, topicId]
    };
    
    setPrefs(newPrefs);
    
    savePrefs(newPrefs);
  }, [prefs, userId, accessToken]);

  const disableAutoJump = useCallback(async (topicId: string) => {
    // Check if already disabled to avoid unnecessary calls
    if (prefs.disabledAutoJumpTopicIds?.includes(topicId)) return;

    // Optimistic update
    const newPrefs = {
      ...prefs,
      disabledAutoJumpTopicIds: [...(prefs.disabledAutoJumpTopicIds || []), topicId]
    };
    
    setPrefs(newPrefs);
    
    savePrefs(newPrefs);
  }, [prefs, userId, accessToken]);

  const savePrefs = async (newPrefs: EducationPrefs) => {
    // Update local cache immediately
    localStorage.setItem(`education_prefs_cache:${userId}`, JSON.stringify(newPrefs));
    
    // Update backend
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/education-prefs/${userId}`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify(newPrefs),
        }
      );
    } catch (error) {
      console.error('Failed to save education prefs to backend', error);
      // We don't roll back state because local cache is still valid for this session
    }
  };

  const isTopicDismissed = useCallback((topicId: string) => {
    return prefs.dismissedTopicIds.includes(topicId);
  }, [prefs]);

  const isAutoJumpDisabled = useCallback((topicId: string) => {
    return !!prefs.disabledAutoJumpTopicIds?.includes(topicId);
  }, [prefs]);

  return {
    prefs,
    loading,
    dismissTopic,
    disableAutoJump,
    isTopicDismissed,
    isAutoJumpDisabled
  };
}